import store from '../stores/index.js';
import * as Sentry from "@sentry/vue";

const LogLevel = {
    NONE: 0,
    ERROR: 1,
    WARN: 2,
    INFO: 3,
    DEBUG: 4,
    TRACE: 5,
};

const fingerprintSeparator = ">>>";

class PublicLogger {
    constructor() {
        this.internalLogger = new InternalLogger();
    }

    error(message, ...args) {
        this.internalLogger.error(message, ...args);
    }

    warn(message, ...args) {
        this.internalLogger.warn(message, ...args);
    }

    info(message, ...args) {
        this.internalLogger.info(message, ...args);
    }

    debug(message, ...args) {
        this.internalLogger.debug(message, ...args);
    }

    trace(message, ...args) {
        this.internalLogger.trace(message, ...args);
    }

    withLevel(level, message, ...args) {
        this.internalLogger.log(level, message, ...args);
    }
}

class InternalLogger {
    constructor() {
        const envLoggingLevel = process.env.VUE_APP_LOGGING_LEVEL;
        if (!envLoggingLevel) {
            this.logLevel = LogLevel.ERROR;
        } else {
            this.logLevel = this.getLogLevelFromString(envLoggingLevel);
        }
    }

    getLogLevelFromString(logLevelString) {
        switch (logLevelString) {
            case 'NONE':
                return LogLevel.NONE;
            case 'ERROR':
                return LogLevel.ERROR;
            case 'WARN':
                return LogLevel.WARN;
            case 'INFO':
                return LogLevel.INFO;
            case 'DEBUG':
                return LogLevel.DEBUG;
            case 'TRACE':
                return LogLevel.TRACE;
        }
        console.error(`Invalid log level ${logLevelString} provided. Defaulting to error.`);
        return LogLevel.ERROR;
    }

    error(message, ...args) {
        this.log(LogLevel.ERROR, message, ...args);
    }

    warn(message, ...args) {
        this.log(LogLevel.WARN, message, ...args);
    }

    info(message, ...args) {
        this.log(LogLevel.INFO, message, ...args);
    }

    debug(message, ...args) {
        this.log(LogLevel.DEBUG, message, ...args);
    }

    trace(message, ...args) {
        this.log(LogLevel.TRACE, message, ...args);
    }

    log(level, message, ...args) {
        const error = this.extractErrorArgument(message, args);
        const metadata = this.extractMetadata(error);

        const enhancedConsoleMessage = this.enhanceConsoleMessage(message, metadata);
        const sanitizedArgs = this.sanitizeArgs(args);
        this.writeToConsole(level, enhancedConsoleMessage, ...sanitizedArgs);

        this.sendToSentry(level, message, metadata, error);
    }

    extractErrorArgument(message, args) {
        // Errors should be the final argument when provided, if not check remaining arguments
        let error = null;
        if (args && args.length > 0) {
            for (let i = args.length - 1; i >= 0; i--) {
                if (args[i] instanceof Error) {
                    error = args[i];
                    break;
                }
            }
        }
        // The error may have been passed as the message argument instead
        if (!error && message instanceof Error) {
            error = message;
        }
        return error;
    }

    extractMetadata(error) {
        return {
            ...this.extractAxiosErrorInfo(error),
            ...this.extractStateInfo(),
        }
    }

    extractAxiosErrorInfo(error) {
        let correlationId = null;
        let responseStatus = null;
        let requestMethod = null;

        if (error && error.isAxiosError) {
            if (error.response) {
                if (error.response.headers) {
                    correlationId = error.response.headers['x-correlation-id'] || null;
                }
                responseStatus = error.response.status || null;
            }
            if (error.config) {
                requestMethod = error.config.method || null;
            }
        }

        return {
            correlationId,
            responseStatus,
            requestMethod,
        };
    }

    extractStateInfo() {
        let accountId = null;
        let agencyId = null;
        let clientId = null;
        let userId = null;

        if (store && store.state) {
            if (store.state.user) {
                userId = store.state.user.id || null;
            }

            if (store.state.context) {
                accountId = store.state.context.account.id || null;
                agencyId = store.state.context.agency.id || null;
                clientId = store.state.context.client.id || null;
            }
        }

        return {
            userId,
            accountId,
            agencyId,
            clientId,
        };
    }

    enhanceConsoleMessage(message, metadata) {
        let enhancedConsoleMessage = `${message} ${fingerprintSeparator}`;
        if (metadata.correlationId) {
            // Surround prefixes with []
            enhancedConsoleMessage = `${enhancedConsoleMessage} [${metadata.correlationId}]`;
        }
        return enhancedConsoleMessage;
    }

    sanitizeArgs(args) {
        let sanitizedArgs = [];
        if (args && args.length > 0) {
            sanitizedArgs = args.map(arg => {
                if (arg instanceof Error) {
                    // removes webpack stack traces from console
                    return arg.message;
                }
                return arg;
            });
        }
        return sanitizedArgs;
    }

    writeToConsole(level, enhancedConsoleMessage, ...sanitizedArgs) {
        switch (level) {
            case LogLevel.ERROR:
                if (this.logLevel >= LogLevel.ERROR) {
                    console.error(enhancedConsoleMessage, ...sanitizedArgs);
                }
                break;
            case LogLevel.WARN:
                if (this.logLevel >= LogLevel.WARN) {
                    console.warn(enhancedConsoleMessage, ...sanitizedArgs);
                }
                break;
            case LogLevel.INFO:
                if (this.logLevel >= LogLevel.INFO) {
                    console.info(enhancedConsoleMessage, ...sanitizedArgs);
                }
                break;
            case LogLevel.DEBUG:
                if (this.logLevel >= LogLevel.DEBUG) {
                    console.debug(enhancedConsoleMessage, ...sanitizedArgs);
                }
                break;
            case LogLevel.TRACE:
                if (this.logLevel >= LogLevel.TRACE) {
                    console.trace(enhancedConsoleMessage, ...sanitizedArgs);
                }
                break;
            default:
                if (this.logLevel >= LogLevel.ERROR) {
                    console.error(`Invalid log level ${level} provided. Defaulting to error.`);
                    console.error(enhancedConsoleMessage, ...sanitizedArgs);
                }
                break;
        }
    }

    sendToSentry(level, message, metadata, error) {
        let sentryLogLevel = null;
        switch (level) {
            case LogLevel.ERROR:
                sentryLogLevel = "error";
                break;
            case LogLevel.WARN:
                sentryLogLevel = "warning";
                break;
            case LogLevel.INFO:
                sentryLogLevel = "info";
                break;
        }
        if (!sentryLogLevel) {
            return;
        }


        Sentry.withScope((scope) => {
            scope.setLevel(sentryLogLevel);

            if (metadata.userId) {
                scope.setUser({id: metadata.userId});
            }

            if (metadata.accountId) {
                scope.setTag("accountId", metadata.accountId);
            }
            if (metadata.agencyId) {
                scope.setTag("agencyId", metadata.agencyId);
            }
            if (metadata.clientId) {
                scope.setTag("clientId", metadata.clientId);
            }
            if (metadata.responseStatus) {
                scope.setTag("responseStatus", metadata.responseStatus);
            }
            if (metadata.requestMethod) {
                scope.setTag("requestMethod", metadata.requestMethod);
            }
            if (metadata.correlationId) {
                scope.setTag("correlationId", metadata.correlationId);
            }

            if (error) {
                error.message = `${message} ${fingerprintSeparator} ${error.message}`;
                scope.captureException(error);
            } else {
                const errorMessage = new ErrorMessage(`${message} ${fingerprintSeparator}`);
                scope.captureException(errorMessage);
            }
        });
    }
}

class ErrorMessage extends Error {
    constructor(message) {
        super(message);
    }
}

const log = new PublicLogger();
export default log;
export {
    LogLevel,
    fingerprintSeparator
};
